class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.scrollTimeout = null;
    this.productId = null;
    this.galleryMode = null;
    this.productHandle = null;
    this.sectionId = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.thumbnails = this.querySelectorAll(".gallery-thumbnail");
    this.mainItems = this.querySelectorAll(".gallery-main-item");
    this.mainContainer = this.querySelector(".gallery-main");
    this.thumbnailsContainer = this.querySelector(".gallery-thumbnails");

    // Get product data from the page
    const productForm = document.querySelector('.product-form[data-product-id]');
    if (productForm) {
      this.productId = productForm.dataset.productId;
    }

    // Get product handle and section ID for Section Rendering API
    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;

    // Get gallery mode
    this.galleryMode = this.dataset.galleryMode || 'default';

    this.setupThumbnailNavigation();
    this.setupScrollTracking();
    this.setupResizeHandler();
    this.setupVariantChangeListener();
  }

  setupThumbnailNavigation() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => {
        this.navigateToImage(index);
        this.setActiveThumbnail(index);
      });
    });
  }

  navigateToImage(index) {
    const targetItem = this.mainItems[index];
    if (!targetItem) return;

    if (window.innerWidth >= 768) {
      // Desktop: Scroll page to target image with buffer at top
      const offsetTop = targetItem.getBoundingClientRect().top + window.pageYOffset;

      // Calculate dynamic buffer based on header height
      const header = document.querySelector('.shopify-section.section-header');
      const buffer = header ? header.offsetHeight : 140;

      window.scrollTo({
        top: offsetTop - buffer,
        behavior: "smooth",
      });
    } else {
      // Mobile: Scroll horizontal container to target image
      if (this.mainContainer) {
        const scrollLeft = targetItem.offsetLeft - 20;
        this.mainContainer.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }

  setActiveThumbnail(activeIndex) {
    this.thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle("active", index === activeIndex);
    });
  }

  trackDesktopScroll() {
    if (window.innerWidth < 768) return;

    const viewportCenter = window.pageYOffset + window.innerHeight / 2;
    let activeItem = null;
    let minDistance = Infinity;

    this.mainItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemTop = rect.top + window.pageYOffset;
      const itemBottom = itemTop + rect.height;
      const itemCenter = itemTop + rect.height / 2;

      if (itemTop <= viewportCenter && itemBottom >= window.pageYOffset) {
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          activeItem = index;
        }
      }
    });

    if (activeItem !== null) {
      this.setActiveThumbnail(activeItem);
    }
  }

  trackMobileScroll() {
    if (window.innerWidth >= 768 || !this.mainContainer) return;

    const scrollLeft = this.mainContainer.scrollLeft;
    const containerWidth = this.mainContainer.clientWidth;
    const centerPoint = scrollLeft + containerWidth / 2;

    let activeItem = null;
    let minDistance = Infinity;

    this.mainItems.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const itemCenter = itemLeft + item.offsetWidth / 2;

      if (itemLeft <= centerPoint && itemRight >= scrollLeft) {
        const distance = Math.abs(itemCenter - centerPoint);
        if (distance < minDistance) {
          minDistance = distance;
          activeItem = index;
        }
      }
    });

    if (activeItem !== null) {
      this.setActiveThumbnail(activeItem);
    }
  }

  setupScrollTracking() {
    const handleScroll = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.trackDesktopScroll(), 100);
    };

    const handleMobileScroll = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.trackMobileScroll(), 100);
    };

    window.addEventListener("scroll", handleScroll);
    
    if (this.mainContainer) {
      this.mainContainer.addEventListener("scroll", handleMobileScroll);
    }

    // Store references for cleanup
    this.handleScroll = handleScroll;
    this.handleMobileScroll = handleMobileScroll;
  }

  setupResizeHandler() {
    const handleResize = () => {
      setTimeout(() => {
        if (window.innerWidth >= 768) {
          this.trackDesktopScroll();
        } else {
          this.trackMobileScroll();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    this.handleResize = handleResize;
  }

  /**
   * Listen for variant changes to update gallery image
   */
  setupVariantChangeListener() {
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });
  }

  /**
   * Handle variant change - navigate to variant's featured media or fetch new gallery
   */
  handleVariantChange(variant) {
    // In variant_gallery mode, use Section Rendering API to fetch fresh gallery
    if (this.galleryMode === 'variant_gallery') {
      this.loadVariantGalleryData(variant);
      return;
    }

    // Default behavior: navigate to variant's featured media
    const featuredMediaId = variant.featured_media?.id;

    if (!featuredMediaId) {
      return;
    }

    // Find the thumbnail and main item with this media ID
    const targetThumbnail = Array.from(this.thumbnails).find(
      thumb => thumb.dataset.mediaId === featuredMediaId.toString()
    );

    const targetMainItem = Array.from(this.mainItems).find(
      item => item.dataset.mediaId === featuredMediaId.toString()
    );

    if (!targetThumbnail || !targetMainItem) {
      console.warn('ProductGallery: Could not find media elements for', featuredMediaId);
      return;
    }

    // Get the index of the target item
    const targetIndex = parseInt(targetThumbnail.dataset.index) - 1; // index is 1-based in Liquid

    // Navigate to the image
    this.navigateToImage(targetIndex);
    this.setActiveThumbnail(targetIndex);
  }

  /**
   * Fetch and replace gallery HTML using Shopify's Section Rendering API
   */
  async loadVariantGalleryData(variant) {
    if (!this.productHandle || !this.sectionId || !variant?.id) {
      console.warn('ProductGallery: Missing required data for Section Rendering API', {
        productHandle: this.productHandle,
        sectionId: this.sectionId,
        variantId: variant?.id
      });
      return;
    }

    try {
      // Build Section Rendering API URL
      const url = `/products/${this.productHandle}?section_id=${this.sectionId}&variant=${variant.id}`;

      // Fetch the section HTML
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Section fetch failed: ${response.status}`);
      }

      const html = await response.text();

      // Parse the HTML to extract the gallery
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGallery = doc.querySelector('product-gallery');

      if (!newGallery) {
        console.warn('ProductGallery: Could not find gallery in fetched section');
        return;
      }

      // Get the gallery container (preserve the wrapper)
      const newGalleryContainer = newGallery.querySelector('.gallery-container');
      const currentGalleryContainer = this.querySelector('.gallery-container');

      if (!newGalleryContainer || !currentGalleryContainer) {
        console.warn('ProductGallery: Could not find gallery container');
        return;
      }

      // Replace the gallery container content
      currentGalleryContainer.innerHTML = newGalleryContainer.innerHTML;

      // Re-query elements after DOM update
      this.thumbnails = this.querySelectorAll(".gallery-thumbnail");
      this.mainItems = this.querySelectorAll(".gallery-main-item");
      this.mainContainer = this.querySelector(".gallery-main");
      this.thumbnailsContainer = this.querySelector(".gallery-thumbnails");

      // Re-setup event listeners
      this.setupThumbnailNavigation();

      // Navigate to first image
      this.navigateToImage(0);
      this.setActiveThumbnail(0);

    } catch (error) {
      console.error('ProductGallery: Failed to fetch variant gallery', error);
    }
  }

  cleanup() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }

    if (this.handleMobileScroll && this.mainContainer) {
      this.mainContainer.removeEventListener("scroll", this.handleMobileScroll);
    }

    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
  }
}

customElements.define('product-gallery', ProductGallery);
